import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
} from '@mui/material'
import { diffChars } from 'diff'
import type * as React from 'react'
import { useState } from 'react'
import type { ConversationInfo } from '../interfaces/ConversationIf'
import { TextDiff } from '../util/TextDiff'
import { detectTextDirection } from '../util/TextDirection'

type ConversationListProps = {
  editMode: boolean
  items: ConversationInfo[]
  setItems: React.Dispatch<React.SetStateAction<ConversationInfo[]>>
  setIsButtonDisabled: React.Dispatch<React.SetStateAction<boolean>>
  isImageDeleted: boolean
}

export const ConversationList: React.FC<ConversationListProps> = ({
  editMode,
  items,
  setItems,
  setIsButtonDisabled,
  isImageDeleted,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [hoveredId, setHoveredId] = useState<string | null>(null)

  const handleEditClick = (id: string) => {
    if (editingId == null) {
      setEditingId(id)
    }
  }

  const handleMouseEnter = (id: string) => {
    if (editMode) {
      setHoveredId(id)
    }
  }
  const handleMouseLeave = () => {
    setHoveredId(null)
  }
  const handleCloseClick = () => {
    const updateItems = items.map(item => {
      return {
        ...item,
        text_being_edited: item.text_editing_temporary_save,
      }
    })
    setItems(updateItems)
    setIsButtonDisabled(true)
    updateItems.forEach(Items => {
      if (
        diffChars(Items.text_before_editing!, Items.text_being_edited!).length !== 1 ||
        Items.text_being_edited! === '' ||
        isImageDeleted === true
      ) {
        setIsButtonDisabled(false)
      }
    })
    setHoveredId(null)
    setEditingId(null)
  }

  const handleCheckClick = () => {
    const updateItems = items.map(item => {
      return {
        ...item,
        text_editing_temporary_save: item.text_being_edited,
      }
    })
    setItems(updateItems)
    setIsButtonDisabled(true)
    updateItems.forEach(Items => {
      if (
        diffChars(Items.text_before_editing!, Items.text_being_edited!).length !== 1 ||
        Items.text_being_edited! === '' ||
        isImageDeleted === true
      ) {
        setIsButtonDisabled(false)
      }
    })
    setHoveredId(null)
    setEditingId(null)
  }

  return (
    <TableContainer>
      <Table>
        <TableBody>
          {items.map(
            row =>
              row.text_before_editing !== '' && (
                <TableRow key={row.conversation_id}>
                  <TableCell
                    align={row.speaker === 1 ? 'left' : 'right'}
                    onClick={() => {
                      if (editingId === row.conversation_id) return
                      handleEditClick(row.conversation_id)
                    }}
                    onMouseEnter={() => handleMouseEnter(row.conversation_id)}
                    onMouseLeave={handleMouseLeave}
                    style={{
                      backgroundColor:
                        hoveredId === row.conversation_id ? '#eeeeee' : 'transparent',
                      border: 'none',
                      padding: '16px 32px',
                    }}
                  >
                    {editMode ? (
                      editingId === row.conversation_id ? (
                        <Box>
                          <TextField
                            multiline
                            rows={2}
                            value={row.text_being_edited}
                            onChange={event => {
                              const updateItems = items.map(item =>
                                item.conversation_id === row.conversation_id
                                  ? {
                                      ...item,
                                      text_being_edited: event.target.value,
                                    }
                                  : item,
                              )
                              setItems(updateItems)
                            }}
                            inputProps={{
                              style: { whiteSpace: 'pre-wrap' },
                            }}
                            sx={{
                              font: 'normal normal normal 21px/32px Noto Sans JP',
                              width: 'calc(100% - 64px)',
                            }}
                            size="small"
                          />
                          <CloseIcon
                            sx={{ width: 32, height: 32, paddingBottom: '6px' }}
                            onClick={handleCloseClick}
                          />
                          <CheckIcon
                            sx={{ width: 32, height: 32, paddingBottom: '6px' }}
                            onClick={handleCheckClick}
                          />
                        </Box>
                      ) : (
                        <div
                          style={{
                            font: 'normal normal normal 21px/32px Noto Sans JP',
                            whiteSpace: 'pre-wrap',
                            display: 'block',
                            boxSizing: 'border-box',
                            overflowWrap: 'break-word',
                          }}
                        >
                          {TextDiff(row.text_before_editing!, row.text_being_edited!)}
                        </div>
                      )
                    ) : (
                      <div
                        style={{
                          font: 'normal normal normal 21px/32px Noto Sans JP',
                          whiteSpace: 'pre-wrap',
                          display: 'block',
                          boxSizing: 'border-box',
                          overflowWrap: 'break-word',
                          direction: detectTextDirection(row.text_being_edited!),
                        }}
                      >
                        {row.text_being_edited!}
                      </div>
                    )}
                    {row.original_text && (
                      <div
                        style={{
                          font: 'normal normal normal 14px Noto Sans JP',
                          marginRight: '10px',
                          whiteSpace: 'pre-wrap',
                          direction: detectTextDirection(row.original_text),
                        }}
                      >
                        {row.original_text}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ),
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
